import React from "react";
import _ from "lodash";
import {
  RefreshArrowOutlined,
  PathOutlined,
  ChecklistOutlined,
  WorksheetClipboardOutlined,
  // SchoolOutlined,
  // ColumnsTickOutlined,
} from "@toddle-edu/ds-icons";

// URL segments under /adminPortal. Each one is a real route under
// `routes/admissionUnit/routes/adminPortal/routes/`. Per the admissions
// camelCase rule, multi-word keys use lowerCamelCase.
// UNITS_AND_SCHOOLS is an exception — it lives at /admissions/unitsAndSchools
// (not under /:unitId/adminPortal/) and uses a custom path in getItemPath.
export const ADMIN_PORTAL_ITEM_KEYS = {
  UNITS_AND_SCHOOLS: "unitsAndSchools",
  ADMISSION_CYCLES: "admissionCycles",
  STAGES_AND_STATUSES: "stagesAndStatuses",
  APPLICANT_PIPELINE: "applicantPipeline",
  CHECKLISTS: "checklists",
  FORM_AND_FIELD_SETTINGS: "formAndFieldSettings",
};

const SECTION = {
  GLOBAL: "global",
  CYCLES: "cycles",
  PIPELINE: "pipeline",
  FORMS: "forms",
};

// Per ADMS-256: surface only developed items, clubbed into one section so
// section dividers drop out (buildSideNavigationOptions only inserts a
// divider between adjacent sections). Hidden entries are commented out;
// their routes (and ADMIN_PORTAL_ITEM_KEYS / getItemPath) remain so direct
// URL access keeps working.
const ADMIN_PORTAL_ITEMS = [
  // {
  //   key: ADMIN_PORTAL_ITEM_KEYS.UNITS_AND_SCHOOLS,
  //   section: SECTION.GLOBAL,
  //   labelLocale: "admissions:units_and_schools",
  //   Icon: SchoolOutlined,
  // },
  {
    key: ADMIN_PORTAL_ITEM_KEYS.ADMISSION_CYCLES,
    section: SECTION.CYCLES,
    labelLocale: "admissions:admission_cycles",
    Icon: RefreshArrowOutlined,
  },
  {
    key: ADMIN_PORTAL_ITEM_KEYS.STAGES_AND_STATUSES,
    section: SECTION.CYCLES,
    labelLocale: "admissions:stages_and_statuses",
    Icon: PathOutlined,
  },
  // {
  //   key: ADMIN_PORTAL_ITEM_KEYS.APPLICANT_PIPELINE,
  //   section: SECTION.PIPELINE,
  //   labelLocale: "admissions:applicant_pipeline",
  //   Icon: ColumnsTickOutlined,
  // },
  {
    key: ADMIN_PORTAL_ITEM_KEYS.CHECKLISTS,
    section: SECTION.CYCLES,
    labelLocale: "admissions:checklists",
    Icon: ChecklistOutlined,
  },
  {
    key: ADMIN_PORTAL_ITEM_KEYS.FORM_AND_FIELD_SETTINGS,
    section: SECTION.CYCLES,
    labelLocale: "admissions:form_and_field_settings",
    Icon: WorksheetClipboardOutlined,
  },
];

export const getItemPath = ({ unitId, itemKey }) => {
  if (itemKey === ADMIN_PORTAL_ITEM_KEYS.UNITS_AND_SCHOOLS) {
    return "/admissions/unitsAndSchools";
  }
  // All other items live at /admissions/:unitId/adminPortal/<itemKey>.
  const base = `/admissions/${unitId}/adminPortal`;
  return itemKey ? `${base}/${itemKey}` : base;
};

// Projects the flat items into the tree shape DS SideNavigation expects:
// root → [item, divider, item, item, divider, item, ...]
// Each leaf carries a tier-1 prefix icon. Search filters across all sections;
// dividers between adjacent sections drop out automatically when one is empty.
export const buildSideNavigationOptions = ({ t, searchText = "" }) => {
  const trimmed = _.toLower(searchText.trim());
  const visible = trimmed
    ? _.filter(ADMIN_PORTAL_ITEMS, item =>
        _.includes(_.toLower(t(item.labelLocale)), trimmed)
      )
    : ADMIN_PORTAL_ITEMS;

  // Group preserving insertion order.
  const sectionOrder = [];
  const grouped = {};
  _.forEach(visible, item => {
    if (!grouped[item.section]) {
      grouped[item.section] = [];
      sectionOrder.push(item.section);
    }
    grouped[item.section].push(item);
  });

  const childrenKeys = [];
  const optionEntries = [];
  _.forEach(sectionOrder, (sectionId, index) => {
    if (index > 0) {
      const dividerKey = `divider-${sectionId}`;
      childrenKeys.push(dividerKey);
      optionEntries.push({ key: dividerKey, isDivider: true, parent: "root" });
    }
    _.forEach(grouped[sectionId], item => {
      childrenKeys.push(item.key);
      optionEntries.push({
        key: item.key,
        title: t(item.labelLocale),
        tier: 1,
        parent: "root",
        prefix: <item.Icon variant="subtle" />,
      });
    });
  });

  return [{ key: "root", children: childrenKeys }, ...optionEntries];
};
